import { Signal, computed, inject, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatCheckboxChange } from "@angular/material/checkbox";

import {
  VaultExplorerService,
  VaultItemGroup,
} from "@bitwarden/web-vault/app/services/dali/vault-explorer.service";
import {
  StoreModelNameByVaultCode,
  VaultCodeEnum,
} from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { StoreData } from "@bitwarden/web-vault/app/models/store/store.data";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { VaultDuplicateItemsDialogComponent } from "../vault-duplicate-items-dialog.component";
import { VaultDeduplicationHelpDialogComponent } from "../vault-deduplication-help-dialog.component";
import {
  VaultCleanupResultDialogComponent,
  CleanupGroupResult,
} from "../vault-cleanup-result-dialog.component";
import { DuplicatesService } from "../duplicates.service";

/**
 * Abstract base class for per-type vault data components.
 * Provides shared duplicate detection, deletion, and cleanup logic.
 * Concrete subclasses supply `_vaultType` and may override `filterGroups`.
 */
export abstract class VaultDataTypeBase {
  protected readonly vaultExplorer = inject(VaultExplorerService);
  protected readonly duplicatesService = inject(DuplicatesService);
  protected readonly dialog = inject(MatDialog);
  protected readonly confirmationService = inject(ConfirmationDialogService);

  /** The VaultCodeEnum for this component — provided by the child via Angular input(). */
  protected abstract readonly _vaultType: Signal<VaultCodeEnum>;

  readonly vaultFilterToggleActiveTwClasses = "!tw-bg-success !tw-text-dt-text-headings";

  readonly vaultDuplicatesOnly = signal(false);
  readonly vaultNotDuplicatesOnly = signal(false);
  readonly vaultIncludeIdForDedup = signal(false);
  readonly vaultCleanupCount = signal<number | null>(null);

  readonly vaultCleanupButtonLabel = computed(() => {
    const n = this.vaultCleanupCount();
    return n && n > 0 ? `Clean up ${n}` : "Clean up";
  });

  readonly vaultItemGroups = computed((): VaultItemGroup[] => {
    const type = this._vaultType();
    if (!type) {
      return [];
    }
    return this.duplicatesService.getGroupedItems(type, this.vaultIncludeIdForDedup());
  });

  readonly vaultDisplayedItemGroups = computed((): VaultItemGroup[] => {
    return this.filterGroups(this.vaultItemGroups());
  });

  readonly vaultDuplicateGroups = computed(() => this.vaultItemGroups().filter((g) => g.count > 1));

  readonly vaultTotalItems = computed(() =>
    this.vaultDisplayedItemGroups().reduce((sum, g) => sum + g.count, 0),
  );

  readonly vaultDisplayedUniqueCount = computed(() => this.vaultDisplayedItemGroups().length);

  readonly vaultTotalDuplicateRecordsToRemove = computed(() =>
    this.vaultDuplicateGroups().reduce((sum, g) => sum + (g.count - 1), 0),
  );

  /**
   * Override in subclasses to add type-specific filters.
   * Always call `super.filterGroups(groups)` first.
   */
  protected filterGroups(groups: VaultItemGroup[]): VaultItemGroup[] {
    if (this.vaultDuplicatesOnly()) {
      groups = groups.filter((g) => g.count > 1);
    } else if (this.vaultNotDuplicatesOnly()) {
      groups = groups.filter((g) => g.count === 1);
    }
    return groups;
  }

  onDuplicatesOnlyChange(ev: MatCheckboxChange): void {
    this.vaultDuplicatesOnly.set(ev.checked);
    if (ev.checked) {
      this.vaultNotDuplicatesOnly.set(false);
    }
  }

  onNotDuplicatesOnlyChange(ev: MatCheckboxChange): void {
    this.vaultNotDuplicatesOnly.set(ev.checked);
    if (ev.checked) {
      this.vaultDuplicatesOnly.set(false);
    }
  }

  onIncludeIdForDedupChange(ev: MatCheckboxChange): void {
    this.vaultIncludeIdForDedup.set(ev.checked);
  }

  onCleanupCountInput(ev: Event): void {
    const el = ev.target as HTMLInputElement | null;
    const raw = el?.value ?? "";
    const parsed = parseInt(raw, 10);
    this.vaultCleanupCount.set(!isNaN(parsed) && parsed > 0 ? parsed : null);
  }

  openDeduplicationHelpDialog(): void {
    this.dialog.open(VaultDeduplicationHelpDialogComponent, {
      width: "min(520px, 95vw)",
      maxHeight: "90vh",
    });
  }

  openDuplicatesModal(group: VaultItemGroup): void {
    this.dialog.open(VaultDuplicateItemsDialogComponent, {
      width: "min(900px, 95vw)",
      maxHeight: "90vh",
      data: { items: [...group.all] },
    });
  }

  vaultItemLabel(group: VaultItemGroup): string {
    const id = group.representative.id?.slice(0, 12) ?? "?";
    const suffix = group.count > 1 ? ` (${group.count})` : "";
    return `${id}${suffix}`;
  }

  vaultItemSummary(group: VaultItemGroup): string {
    try {
      const {
        id: _id,
        vid: _vid,
        dc: _dc,
        dm: _dm,
        v: _v,
        del: _del,
        ...rest
      } = group.representative as any;
      return JSON.stringify(rest, null, 2);
    } catch {
      return "—";
    }
  }

  async performCleanup(): Promise<void> {
    const type = this._vaultType();
    if (!type) {
      return;
    }
    const allGroups = this.vaultDuplicateGroups();
    if (allGroups.length === 0) {
      return;
    }
    const count = this.vaultCleanupCount() ?? allGroups.length;
    const groupsToClean = allGroups.slice(0, count);

    const results: CleanupGroupResult[] = [];
    const allToDelete: StoreData[] = [];

    for (const group of groupsToClean) {
      const sorted = [...group.all].sort(
        (a, b) => new Date(b.dc).getTime() - new Date(a.dc).getTime(),
      );
      const kept = sorted[0];
      const deleted = sorted.slice(1);
      results.push({ kept, deleted });
      allToDelete.push(...deleted);
    }

    if (allToDelete.length === 0) {
      return;
    }

    try {
      const modelName = StoreModelNameByVaultCode[type];
      await this.vaultExplorer.deleteVaultItems(modelName, allToDelete);
      await this.vaultExplorer.reloadVaultDataForType(type);
    } catch {
      this.confirmationService.notify(NotificationEnum.error, "error", "Cleanup failed.", false);
      return;
    }

    this.dialog.open(VaultCleanupResultDialogComponent, {
      width: "min(1100px, 95vw)",
      maxHeight: "90vh",
      data: { groups: results },
    });
  }

  async removeAllDuplicates(): Promise<void> {
    const type = this._vaultType();
    if (!type || this.vaultDuplicateGroups().length === 0) {
      return;
    }
    const groups = this.vaultDuplicateGroups().length;
    const dupRecords = this.vaultTotalDuplicateRecordsToRemove();
    const confirmed = await this.confirmationService.confirmFor(
      ConfirmationEnum.removeAllVaultDuplicatesForType,
      {
        messageReplacement: `Remove ${dupRecords} duplicate record(s) across ${groups} group(s)? The newest copy per group (by date created) is kept.`,
      },
    );
    if (!confirmed) {
      return;
    }

    try {
      const n = await this.vaultExplorer.deleteAllDuplicatesForType(
        type,
        this.vaultIncludeIdForDedup(),
      );
      await this.vaultExplorer.reloadVaultDataForType(type);
      this.confirmationService.notify(
        NotificationEnum.success,
        "Duplicates removed",
        `${n} duplicate record(s) removed.`,
        false,
      );
    } catch {
      this.confirmationService.notify(
        NotificationEnum.error,
        "error",
        "Failed to remove duplicates.",
        false,
      );
    }
  }

  async onItemClick(group: VaultItemGroup): Promise<void> {
    if (group.count <= 1) {
      return;
    }
    const type = this._vaultType();
    if (!type) {
      return;
    }

    const confirmed = await this.confirmationService.confirmFor(
      ConfirmationEnum.deletingVaultDuplicates,
      {
        messageReplacement: `This item has ${group.count} copies. Delete ${group.count - 1} duplicate(s) and keep the newest?`,
      },
    );
    if (!confirmed) {
      return;
    }

    try {
      const deleted = await this.vaultExplorer.deleteDuplicates(type, group);
      this.confirmationService.notify(
        NotificationEnum.success,
        "Duplicates Removed",
        `${deleted} duplicate(s) deleted. Refresh to see the updated vault.`,
        false,
      );
      await this.vaultExplorer.reloadVaultDataForType(type);
    } catch {
      this.confirmationService.notify(
        NotificationEnum.error,
        "error",
        "Failed to delete duplicates.",
        false,
      );
    }
  }
}
